import React, { useMemo } from "react";
import { Edit, Trash2, Eye, Plus, Download } from "lucide-react";
import { useInstitutions } from "./useInstitutions";
import { useInstitutionModal } from "./useInstitutionModal";
import ActionPanel from "@/components/layout/actionPanel/ActionPanel";
import AlertMessage from "@/components/layout/message/AlertMessage";
import PaginationControls from "@/components/layout/paginationControls/PaginationControls";
import InstitutionFormModal from "../institutionFormModal/InstitutionFormModal";
import InstitutionTable from "../institutionTable/InstitutionTable";
import type { institution } from "../userFormModal/types.props";
import type { institutionSectionProps } from "./types.props";
import { useInstitutionReport } from "./useInstitutionReport";
import SimulationSelectorModal from "./SimulationSelectorModal";

const PAGE_SIZE = 15;

const InstitutionSection: React.FC<institutionSectionProps> = (
    { refreshStats },
) => {
    const {
        data: institutions,
        count,
        currentPage,
        filters,
        isLoading,
        error: institutionsError,
        create,
        update,
        remove,
        setPage,
        setFilters,
    } = useInstitutions();

    const {
        isModalOpen: isInstitutionModalOpen,
        isLoading: isReportLoading,
        simulations,
        openReportModal,
        closeReportModal,
        handleDownload,
        error: reportError,
    } = useInstitutionReport();

    const {
        isModalOpen,
        editingInstitution,
        apiError,
        handleOpenModal,
        handleCloseModal,
        handleSubmit: handleSubmitInstitution,
        handleDeleteInstitution,
        handleViewDashboard,
    } = useInstitutionModal({ create, update, remove, refreshStats });

    const institutionActions = useMemo(
        () => [
            {
                label: "Modificar",
                handler: (inst: institution) => handleOpenModal(inst),
                variant: "secondary" as const,
                icon: <Edit size={16} />,
            },
            {
                label: "Ver dashboard",
                handler: handleViewDashboard,
                variant: "primary" as const,
                icon: <Eye size={16} />,
            },
            {
                label: "Eliminar",
                handler: handleDeleteInstitution,
                variant: "danger" as const,
                icon: <Trash2 size={16} />,
            },
            {
                label: "Exportar reporte",
                handler: (inst: institution) => openReportModal(inst.id),
                variant: "secondary" as const,
                icon: <Download size={16} />,
            },
        ],
        [handleOpenModal, handleViewDashboard, handleDeleteInstitution, openReportModal],
    );

    const actions = [
        {
            key: "add",
            icon: <Plus size={16} />,
            label: "Añadir institución",
            variant: "primary" as const,
            onClick: () => handleOpenModal(null),
            tooltip: "Crear una nueva institución",
        },
    ];

    return (
        <>
            <ActionPanel
                placeholder="Buscar por nombre de institución..."
                searchTerm={filters.search}
                onSearchChange={(query) => setFilters({ search: query })}
                actions={actions}
            />

            <AlertMessage type="error" title="Error">
                {institutionsError?.generalMessage || apiError?.generalMessage}
            </AlertMessage>

            <AlertMessage type="error" title="Error al generar reporte">
                {reportError?.generalMessage}
            </AlertMessage>

            <InstitutionTable
                data={institutions}
                isLoading={isLoading}
                actions={institutionActions}
            />

            {count > PAGE_SIZE && (
                <PaginationControls
                    currentPage={currentPage}
                    totalCount={count}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                />
            )}

            <InstitutionFormModal
                isOpen={isModalOpen}
                isLoading={isLoading}
                initialData={editingInstitution || undefined}
                apiError={apiError}
                onClose={handleCloseModal}
                onSubmit={handleSubmitInstitution}
            />

            <SimulationSelectorModal
                isOpen={isInstitutionModalOpen}
                isLoading={isReportLoading}
                simulations={simulations}
                onClose={closeReportModal}
                onConfirm={handleDownload}
            />
        </>
    );
};

export default InstitutionSection;
